import React from "react";
import "./DatabaseMatch.css";

/**
 * DatabaseMatch component to display potential database matches for pending user requests
 *
 * @param {Object} props
 * @param {Object} props.dbUser - Database user match object
 * @param {boolean} props.isSelected - Whether this match is selected
 * @param {boolean} props.isEditable - Whether the match is currently being edited
 * @param {Object} props.editedFields - Fields that have been edited
 * @param {boolean} props.isSaving - Whether a save operation is in progress
 * @param {Object} props.saveMessage - Message to display about save operation
 * @param {Function} props.onSelect - Function to call when selecting this match
 * @param {Function} props.onEdit - Function to call to toggle edit mode
 * @param {Function} props.onFieldChange - Function to call when field value changes
 * @param {Function} props.onSave - Function to call to save edited data
 * @returns {JSX.Element}
 */
const DatabaseMatch = ({
  dbUser,
  isSelected,
  isEditable,
  editedFields,
  isSaving,
  saveMessage,
  onSelect,
  onEdit,
  onFieldChange,
  onSave,
  groupmeId,
}) => {
  return (
    <div key={dbUser.userId} className="db-user-card">
      <div className="db-user-details">
        <div>
          <strong>User ID:</strong> {dbUser.userId}
        </div>
        {isEditable ? (
          // Editable fields when in edit mode
          <>
            <div className="editable-field">
              <strong>Name:</strong>
              <input
                type="text"
                value={
                  editedFields.name !== undefined
                    ? editedFields.name
                    : dbUser.name || ""
                }
                onChange={(e) => onFieldChange("name", e.target.value)}
              />
            </div>
            <div className="editable-field">
              <strong>Phone:</strong>
              <input
                type="text"
                value={
                  editedFields.phone !== undefined
                    ? editedFields.phone
                    : dbUser.phone || ""
                }
                onChange={(e) => onFieldChange("phone", e.target.value)}
              />
            </div>
            <div className="editable-field">
              <strong>Sex:</strong>
              <select
                value={
                  editedFields.zsex !== undefined
                    ? editedFields.zsex
                    : dbUser.zsex || ""
                }
                onChange={(e) => onFieldChange("zsex", e.target.value)}
              >
                <option value="">Not specified</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            {/* Editable form for other fields */}
            {Object.entries(dbUser)
              .filter(
                ([key, _]) => !["userId", "name", "phone", "zsex"].includes(key)
              )
              .map(([key, value]) => (
                <div key={key} className="editable-field">
                  <strong>{key}:</strong>
                  <input
                    type="text"
                    value={
                      editedFields[key] !== undefined
                        ? editedFields[key]
                        : value !== null
                        ? String(value)
                        : ""
                    }
                    onChange={(e) => onFieldChange(key, e.target.value)}
                  />
                </div>
              ))}
            {saveMessage && (
              <div className={`save-message ${saveMessage.type}`}>
                {saveMessage.text}
              </div>
            )}
          </>
        ) : (
          // Read-only display when not in edit mode
          <>
            <div>
              <strong>Name:</strong> {dbUser.name || "N/A"}
            </div>
            <div>
              <strong>Phone:</strong> {dbUser.phone || "N/A"}
            </div>
            <div>
              <strong>Sex:</strong> {dbUser.zsex || "N/A"}
            </div>
            {/* Display other relevant fields */}
            {Object.entries(dbUser)
              .filter(
                ([key, _]) => !["userId", "name", "phone", "zsex"].includes(key)
              )
              .map(([key, value]) => (
                <div key={key}>
                  <strong>{key}:</strong>{" "}
                  {typeof value === "object"
                    ? JSON.stringify(value)
                    : String(value)}
                </div>
              ))}
          </>
        )}
      </div>
      <div className="db-user-actions">
        <button
          onClick={() => onSelect(groupmeId, dbUser.userId)}
          className={`selection-btn ${isSelected ? "selected" : ""}`}
        >
          {isSelected ? "Deselect" : "Select"}
        </button>

        {/* Edit button */}
        <button
          onClick={() => onEdit(dbUser)}
          className={isEditable ? "edit-active" : "edit"}
        >
          {isEditable ? "Cancel Edit" : "Edit Fields"}
        </button>

        {/* Save button - only show in edit mode */}
        {isEditable && (
          <button
            onClick={() => onSave(groupmeId, dbUser.userId)}
            className="save-btn"
            disabled={isSaving || Object.keys(editedFields).length === 0}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        )}
      </div>
    </div>
  );
};

export default DatabaseMatch;
